import React, { useState } from "react";
import { ASC, DESC, NO_DATA_AVAILABLE } from "../../Constants/Strings";

// tableColumns: Actual Column Names (required) Array of String
// tableRows: Table Rows object (required) Array of objects
// columnLabels: Column Labels to be shown on UI (key value JSON)
// renderField: Formatting on row data function
// sortingColumn: applies sorting on this Column

const CommonTable = ({
  tableColumns,
  tableRows,
  columnLabels = null,
  rowClick = null,
  headerClassName = null,
  renderField = null,
  className = null,
  fieldClick = null,
  sortingColumn = null,
  customError = "",
  ...rest
}) => {
  const defaultClasses = "w-full text-left text-secondary border";
  const classes = `${defaultClasses} ${className}`;
  const [sortOrder, setSortOrder] = useState(DESC);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === ASC ? DESC : ASC);
  };

  const sortRequests = (
    tableRows = [],
    sortBy = sortingColumn,
    sortOrder = ASC
  ) => {
    if (tableRows && tableRows.length > 0) {
      return tableRows.sort((a, b) => {
        const dateA = new Date(a[sortBy]);
        const dateB = new Date(b[sortBy]);

        if (sortOrder === ASC) {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      });
    }
  };

  const sortedRows = sortRequests(tableRows, sortingColumn, sortOrder);
  const error = customError || NO_DATA_AVAILABLE;

  return (
    <>
      {/* Table view for larger screens */}
      <div className="hidden md:block overflow-x-auto">
        <table className={classes} {...rest}>
          <thead
            className={`sticky top-0 text-sm text-secondary bg-gray-50 ${headerClassName}`}
          >
            <tr className="shadow-md">
              {tableColumns.map((key) => (
                <th scope="col" className="px-2 py-3" key={key}>
                  {columnLabels ? columnLabels[key] : key}
                  {key === sortingColumn && (
                    <button onClick={toggleSortOrder} className="ml-2 text-sm">
                      {sortOrder === ASC ? "↑" : "↓"}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows?.map((row, index) => (
              <tr
                key={index}
                className={`border-b ${rowClick ? "cursor-pointer" : ""}`}
                onClick={rowClick ? () => rowClick(row) : null}
              >
                {tableColumns.map((key) => (
                  <td
                    className={`px-2 py-4 text-wrap break-words ${fieldClick ? "cursor-pointer" : ""
                      }`}
                    key={key}
                    onClick={fieldClick ? () => fieldClick(key, row) : null}
                  >
                    <div className="truncate max-w-[200px] overflow-hidden">
                      {renderField ? renderField(key, row) : row[key]}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {!sortedRows || sortedRows?.length === 0 ? (
              <tr>
                <td
                  className="text-lg text-center p-4"
                  colSpan={tableColumns.length}
                >
                  {error}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Mobile view */}
      <div className="block md:hidden space-y-4">
        {sortedRows?.map((row, index) => (
          <div
            key={index}
            className={`border rounded-md text-sm shadow-md p-4 ${rowClick ? "cursor-pointer" : ""
              }`}
            onClick={rowClick ? () => rowClick(row) : null}
          >
            {tableColumns.map((key) => (
              <div key={key} className="flex justify-between py-2 items-center">
                <span className="font-medium text-secondary">
                  {columnLabels && columnLabels[key] ? columnLabels[key] : key}
                  {columnLabels && columnLabels[key] ? ":" : ""}
                </span>
                <span
                  className={`pl-2 text-wrap break-all text-gray-900 ${fieldClick ? "cursor-pointer" : ""
                    }`}
                  onClick={fieldClick ? () => fieldClick(key, row) : null}
                >
                  <div className="truncate max-w-[200px] overflow-hidden text-right">
                    {renderField ? renderField(key, row) : row[key]}
                  </div>
                </span>
              </div>
            ))}
          </div>
        ))}
        {!sortedRows || sortedRows?.length === 0 ? (
          <div className="text-lg text-center h-full p-2">{error}</div>
        ) : null}
      </div>
    </>
  );
};

export default CommonTable;
